import type { ImageProps, Slide, TextProps } from '@features/powerpoint';
import { chartOptionsToBase64Svg } from '@features/powerpoint';
import type {
  ChannelGroupingKey,
  Lci3SalesMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { waterfallChartOptions } from '@features/campaigns';
import { liftCategoriesToWaterfallChartProps } from '@features/lci-report/subdomains/rsl/utils/liftCategoriesToWaterfallChartProps';
import { DEFINITIONS } from '../definitions';
import { themeV2 } from '../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../utils/constants-v2';
import { contentSlideHeaderV2 } from '../../../utils/slide-header-v2';
import {
  splitKeyTakeaways,
  parsedTakeawaysV2,
} from '../../../domain/key-takeaways-v2';
import { keyTakeawaysSparkle } from '../../../images';

type ContributionMetricsSummarySlideV2Props = {
  slideMaster: string;
  overallSalesMetrics: Lci3SalesMetrics;
  metric: ChannelGroupingKey;
  title: string;
  keyTakeaways: string;
};

const createDefinitionText = (
  title: string,
  definition: string,
  y: number
) => ({
  text: [{ text: `${title}: `, options: { bold: true } }, { text: definition }],
  options: {
    h: 0.41,
    w: 11.5,
    x: 0.43,
    y,
    fontSize: 14,
    fontFace: DEFAULT_FONT,
    color: themeV2.colors.metricBlue,
  },
});

export const contributionMetricsSummarySlideV2 = (
  props: ContributionMetricsSummarySlideV2Props
): Slide => {
  const contributionMetricsGraph = chartOptionsToBase64Svg({
    chartOptions: waterfallChartOptions(
      liftCategoriesToWaterfallChartProps(
        props.metric,
        props.overallSalesMetrics
      )
    ),
    width: 600,
    height: 430,
  });

  const images: ImageProps[] = [
    {
      data: contributionMetricsGraph,
      h: 4,
      w: 6,
      x: 0.55,
      y: 1.64,
    },
  ];

  const definitionTexts = DEFINITIONS.map(({ title, definition, y }) =>
    createDefinitionText(title, definition, y)
  );

  const hasKeyTakeaways = props.keyTakeaways.trim().length > 0;

  const keyTakeawaysTexts: TextProps[] = hasKeyTakeaways
    ? [
        {
          text: 'Key Takeaways',
          options: {
            fontFace: DEFAULT_FONT,
            fontSize: 18,
            bold: true,
            color: themeV2.colors.black,
            x: 7.5,
            y: 1.64,
            w: 4,
            h: 0.47,
          },
        },
        {
          text: parsedTakeawaysV2(splitKeyTakeaways(props.keyTakeaways)),
          options: {
            h: 3.4,
            w: 5.3,
            x: 7,
            y: 2.3,
            valign: 'top',
            paraSpaceAfter: 12,
            fontSize: 12,
            fontFace: DEFAULT_FONT,
            color: themeV2.colors.metricBlue,
          },
        },
      ]
    : [];

  return {
    slideMaster: props.slideMaster,
    texts: [
      contentSlideHeaderV2(`Contribution Metrics - ${props.title}`),
      ...definitionTexts,
      ...keyTakeawaysTexts,
    ],
    images: [
      ...images,
      ...(hasKeyTakeaways
        ? [
            {
              path: keyTakeawaysSparkle,
              x: 7,
              y: 1.67,
              h: 0.4,
              w: 0.4,
            },
          ]
        : []),
    ],
    shapes: hasKeyTakeaways
      ? [
          {
            shapeName: 'line' as const,
            options: {
              h: 0,
              w: 5.3,
              x: 7,
              y: 2.15,
              line: {
                color: themeV2.colors.inmarketGreen,
                width: 1,
              },
            },
          },
        ]
      : [],
  };
};
